import React, {useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';

import {useApi} from '../../lib/api';
import {useUrlState} from '../../lib/with-url-state';
import * as tagHelpers from '../../lib/tag-helpers';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Pagination from '../../components/Pagination';
import ListScreen from '../../components/ListScreen';
import ListItem from '../../components/ListItem';
import {T} from '../../components/I18n';
import InventoryTabs from './InventoryTabs';
import InventoryItemModal from './InventoryItemModal';
import {EXCEPTIONS_PATH, itemPath} from './paths';
import {
  PAGE_SIZE,
  usePagedResource,
  useDebouncedSearch,
  useInventoryFilterOptions,
  useTagFilterReady,
  getTagId,
  ItemTags,
  formatQty,
  withUnit,
  getLotLabel,
  getStatusLabel,
  getTransactionItemName,
  getTransactionLotCode,
  getTransactionDate,
  formatDateTime,
  getSubmissionPath,
  Negative,
  PaginationContainer
} from './shared';

const defaultUrlState = {
  sort: 'NEWEST',
  status: 'all',
  form: 'all',
  tag: 'all'
};

function ExceptionsView() {
  const api = useApi();
  const history = useHistory();
  const {search: queryString} = useLocation();
  const itemMatch = useRouteMatch(`${EXCEPTIONS_PATH}/items/:itemId`);
  const [urlState, setUrlState] = useUrlState(defaultUrlState);
  const {searchText, setSearchText, search} = useDebouncedSearch();
  const {forms, tags, tagsLoaded} = useInventoryFilterOptions();

  const {sort, status, form, tag} = urlState;
  const tagId = getTagId(tags, tag);
  const ready = useTagFilterReady(tag, tagId, tagsLoaded, setUrlState);

  const exceptions = usePagedResource(
    page => api.getInventoryExceptions({
      search,
      sort,
      status: status === 'all' ? null : status,
      formId: form === 'all' ? null : form,
      tagId,
      page,
      size: PAGE_SIZE
    }),
    [search, sort, status, form, tagId],
    ready
  );
  const {setPage} = exceptions;

  //a new search or filter starts again from the first page
  useEffect(() => setPage(0), [search, sort, status, form, tagId]);

  const handleCloseItem = useCallback(() => history.push(EXCEPTIONS_PATH + queryString), [history, queryString]);

  const {loading, error, result, page} = exceptions;
  const transactions = (result && result.content) || [];

  return (
    <>
      <ListScreen
        title="Inventory"
        activeLink="Inventory"
        tabs={<InventoryTabs view="exceptions" />}
        placeholder="Search items or lots..."
        loading={!result && !error && <Loading fullScreen />}
        error={error && <Error fullScreen />}
        empty={search
          ? <T isolate>No exceptions match "<T variable="search">{search}</T>".</T>
          : 'There are no exceptions to display. Negative balances and unit conflicts show up here.'}
        items={transactions}
        search={searchText}
        onSearch={e => setSearchText(e.target.value)}
        toolbarActions={[
          <Input id="inventory-exception-sort" label="Sort by:" type="select" value={sort}
                 onChange={e => setUrlState({sort: e.target.value})}>
            <option value="NEWEST">Newest first</option>
            <option value="OLDEST">Oldest first</option>
          </Input>
        ]}
        toolbarFilters={[
          <Input id="inventory-exception-status" label="Type:" type="select" value={status}
                 onChange={e => setUrlState({status: e.target.value})}>
            <option value="all">All Types</option>
            <option value="NEGATIVE">Negative balance</option>
            <option value="CONFLICT">Unit conflict</option>
            <option value="UNRESOLVED_ITEM">Item not identified</option>
            <option value="ERROR">Posting error</option>
          </Input>,
          <Input id="inventory-exception-form" label="Form:" type="select" value={form}
                 onChange={e => setUrlState({form: e.target.value})} style={{maxWidth: '200px', minWidth: '150px'}}>
            <option value="all">All Forms</option>
            {forms.map(f => <option key={f.id} value={f.id} title={f.name}>{f.name}</option>)}
          </Input>,
          <Input id="inventory-exception-tag" label="Tag:" type="select" value={tag}
                 onChange={e => setUrlState({tag: e.target.value})} style={{maxWidth: '200px', minWidth: '120px'}}>
            <option value="all">All Tags</option>
            {tags.map(t => {
              const tagName = tagHelpers.getTagName(t);
              return <option key={tagName} value={tagName}>{tagName}</option>;
            })}
          </Input>
        ]}
        footer={result && result.totalElements > PAGE_SIZE && (
          <PaginationContainer>
            <Pagination
              page={page + 1}
              itemsPerPage={PAGE_SIZE}
              totalItems={result.totalElements}
              disabled={loading}
              onClick={nextPage => setPage(nextPage - 1)}
            />
          </PaginationContainer>
        )}>
        {(transaction, i) => {
          const date = getTransactionDate(transaction);
          const submissionPath = getSubmissionPath(transaction);
          const hasBalance = transaction.currentBalance !== null && transaction.currentBalance !== undefined;
          const hasEntered = transaction.enteredQty !== null && transaction.enteredQty !== undefined;

          return (
            <ListItem
              key={transaction.id}
              first={i === 0}
              options={[
                transaction.itemId && {title: 'View', to: itemPath(EXCEPTIONS_PATH, transaction.itemId) + queryString},
                submissionPath && {title: 'View Submission', to: submissionPath}
              ].filter(Boolean)}>
              <ExceptionContent>
                <strong><T noTranslate>{getTransactionItemName(transaction)}</T></strong>
                <ExceptionSubtitle>
                  <span>{getLotLabel(getTransactionLotCode(transaction))}</span>
                  <span>
                    {getStatusLabel(transaction.status)}
                    {transaction.message && <>: <T noTranslate>{transaction.message}</T></>}
                  </span>
                  <span>
                    {hasEntered && <>Entered: {withUnit(formatQty(transaction.enteredQty), transaction.enteredUnit)}</>}
                    {hasEntered && hasBalance && ' · '}
                    {hasBalance && <>Balance: {transaction.currentBalance < 0
                      ? <Negative>{withUnit(formatQty(transaction.currentBalance), transaction.itemUnit)}</Negative>
                      : withUnit(formatQty(transaction.currentBalance), transaction.itemUnit)}</>}
                  </span>
                  <span>
                    {date && formatDateTime(date)}
                    {transaction.userName && <> · <T noTranslate>{transaction.userName}</T></>}
                    {transaction.sourceHidden && ' · From a form you don\'t have access to'}
                  </span>
                  <ItemTags tags={transaction.itemTags} onSelect={tagName => setUrlState({tag: tagName})} />
                </ExceptionSubtitle>
              </ExceptionContent>
            </ListItem>
          );
        }}
      </ListScreen>
      <InventoryItemModal
        itemId={itemMatch ? itemMatch.params.itemId : null}
        onClose={handleCloseItem}
        onChange={exceptions.reload}
      />
    </>
  );
}

//laid out like a row on the forms list: a bold title over a column of small detail lines
const ExceptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  min-width: 0;

  strong {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ExceptionSubtitle = styled.small`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  color: #666;

  > span {
    display: block;
  }
`;

export default ExceptionsView;
